import { useEffect } from 'react';
import type { PersonaId } from '../types/persona';
import { isIOSPath, presentationPath, standardPath, useWebPath } from './web-presentation';

export const PRODUCT_ROOTS: Record<PersonaId, string> = {
  practitioner: '/practice',
  business: '/business',
  individual: '/personal-tax',
};

export const PRODUCT_VIEW_STORAGE_PREFIX = 'amoye-last-product-view-v1';

function productViewStorageKey(product: PersonaId): string {
  return `${PRODUCT_VIEW_STORAGE_PREFIX}${isIOSPath() ? ':ios' : ''}:${product}`;
}

function rememberedProductView<T extends string>(product: PersonaId, views: readonly T[]): T | undefined {
  try {
    const remembered = window.localStorage.getItem(productViewStorageKey(product));
    return views.includes(remembered as T) ? remembered as T : undefined;
  } catch { return undefined; }
}

function rememberProductView(product: PersonaId, view: string): void {
  try { window.localStorage.setItem(productViewStorageKey(product), view); }
  catch { /* The current URL still preserves navigation when storage is restricted. */ }
}

export function productFromPath(pathname = window.location.pathname): PersonaId | undefined {
  pathname = standardPath(pathname);
  const entry = (Object.entries(PRODUCT_ROOTS) as [PersonaId, string][])
    .find(([, root]) => pathname === root || pathname.startsWith(`${root}/`));
  return entry?.[0];
}

export function productPath(product: PersonaId, view?: string): string {
  const root = presentationPath(PRODUCT_ROOTS[product]);
  return view ? `${root}/${view}` : root;
}

export function navigateProduct(product: PersonaId, view?: string, replace = false): void {
  const path = productPath(product, view);
  if (window.location.pathname === path) return;
  window.history[replace ? 'replaceState' : 'pushState']({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useProductSurfaceRoute<T extends string>(product: PersonaId, views: readonly T[], fallback: T) {
  const pathname = useWebPath();
  const read = (): T => {
    const root = presentationPath(PRODUCT_ROOTS[product], isIOSPath(pathname));
    const segment = pathname.startsWith(`${root}/`)
      ? pathname.slice(root.length + 1).split('/')[0]
      : undefined;
    if (views.includes(segment as T)) return segment as T;
    // Standard product roots are stable Home entry points. The separate /ios
    // presentation keeps its existing last-view restoration behaviour.
    return pathname === root && isIOSPath(pathname) ? rememberedProductView(product, views) ?? fallback : fallback;
  };
  // A single external URL store avoids missing popstate when a parent rerenders
  // and a caller recreates its views array while that same event is dispatching.
  const active = read();

  useEffect(() => {
    if (productFromPath(pathname) !== product) return;
    rememberProductView(product, active);
    if (pathname === productPath(product) && active !== fallback) {
      navigateProduct(product, active, true);
    }
  }, [active, fallback, product, pathname]);

  const navigate = (view: T) => {
    rememberProductView(product, view);
    navigateProduct(product, view);
  };
  return [active, navigate] as const;
}
